import { useEffect, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  BookOpenText,
  Check,
  ClipboardList,
  Clock,
  Droplet,
  ExternalLink,
  FileSignature,
  Hammer,
  Lightbulb,
  MailCheck,
  MailOpen,
  Receipt,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Upload as UploadIcon,
  X,
} from "lucide-react";
import {
  api,
  ApiError,
  type Anomaly,
  type AnomalyOutput,
  type ConditionsExplanationEnvelope,
  type ContractorStatus,
  type ExplainedCondition,
  type FeeRow,
  type FeeSummary,
  type IndemnityState,
  type PreflightOutput,
  type PreflightStep,
  type ProjectDeadlines,
  type RiserShutdownRow,
  type RulesAckState,
} from "../api";
import { Button, Card, Field, cx } from "../ui";

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

// ---------------------------------------------------------------------------
// Project timeline card — surfaces every deadline and countdown that
// applies to this project: earliest possible start (computed from
// rule constraints), board voting window close, owner liability
// policy expiration, earliest pending riser shutdown date.
// ---------------------------------------------------------------------------
export function TimelineCard({
  deadlines,
  categoryCode,
}: {
  deadlines: ProjectDeadlines;
  categoryCode?: "A" | "B" | "C";
}) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const formatCountdown = (
    iso: string | null,
  ): { days: number; tone: "ok" | "warn" | "rejected" | "past" } | null => {
    if (!iso) return null;
    const target = new Date(iso);
    const ms = target.getTime() - today.getTime();
    const days = Math.ceil(ms / 86_400_000);
    if (days < 0) return { days, tone: "past" };
    if (days <= 2) return { days, tone: "rejected" };
    if (days <= 7) return { days, tone: "warn" };
    return { days, tone: "ok" };
  };

  const startCountdown = formatCountdown(deadlines.earliestStartDate);
  const votingCountdown = formatCountdown(deadlines.votingWindowEndsAt);
  const policyCountdown = formatCountdown(
    deadlines.insurancePolicyExpirationDate,
  );
  const riserCountdown = formatCountdown(deadlines.riserShutdownEarliestDate);

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <Clock size={16} /> Timeline & deadlines
      </h2>

      {/* Earliest start */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3 pb-3 border-b border-sand-100">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-ink-400">
            Earliest possible start
          </div>
          <div className="text-ink-800 font-semibold text-[15px]">
            {new Date(deadlines.earliestStartDate).toLocaleDateString()}
          </div>
          <div className="text-[11.5px] text-ink-500 mt-0.5">
            Based on{" "}
            {deadlines.constraints
              .map((c) => `${c.label} (${c.days}d)`)
              .join(" · ")}
            {categoryCode === "A"
              ? ". Decorative work is notification only — actual start is at your discretion."
              : "."}
          </div>
        </div>
        {startCountdown && (
          <CountdownChip
            label={
              startCountdown.tone === "past"
                ? "Eligible now"
                : `${startCountdown.days}d out`
            }
            tone={startCountdown.tone === "past" ? "ok" : startCountdown.tone}
          />
        )}
      </div>

      {/* Voting window */}
      {deadlines.votingWindowEndsAt && (
        <DeadlineRow
          label="Board voting window closes"
          date={deadlines.votingWindowEndsAt}
          countdown={votingCountdown}
          help="14-day window per the Construction Rules. Late ballots aren't counted."
        />
      )}

      {/* Insurance expiration */}
      {deadlines.insurancePolicyExpirationDate && (
        <DeadlineRow
          label="Owner liability policy expires"
          date={deadlines.insurancePolicyExpirationDate}
          countdown={policyCountdown}
          help={
            policyCountdown && policyCountdown.days <= 30
              ? "30-day cancellation notice. Renew now to avoid the $100/mo missing-insurance fine."
              : "Per the Rules, every owner keeps a $500K liability policy on file."
          }
        />
      )}

      {/* Riser shutdown */}
      {deadlines.riserShutdownEarliestDate && (
        <DeadlineRow
          label="Earliest pending riser shutdown"
          date={deadlines.riserShutdownEarliestDate}
          countdown={riserCountdown}
          help="72-hour minimum advance notice required. $20 reschedule fee."
        />
      )}

      {!deadlines.votingWindowEndsAt &&
        !deadlines.insurancePolicyExpirationDate &&
        !deadlines.riserShutdownEarliestDate && (
          <p className="text-[12px] text-ink-500">
            No active deadlines beyond the earliest-start computation above.
          </p>
        )}
    </Card>
  );
}

function DeadlineRow({
  label,
  date,
  countdown,
  help,
}: {
  label: string;
  date: string;
  countdown: { days: number; tone: "ok" | "warn" | "rejected" | "past" } | null;
  help: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap py-2.5 border-b last:border-b-0 border-sand-100">
      <div className="flex-1 min-w-0">
        <div className="text-[12.5px] text-ink-700">
          <span className="text-ink-500">{label}:</span>{" "}
          <span className="font-medium text-ink-800">
            {new Date(date).toLocaleDateString()}
          </span>
        </div>
        <div className="text-[11.5px] text-ink-500 mt-0.5 leading-snug">
          {help}
        </div>
      </div>
      {countdown && (
        <CountdownChip
          label={
            countdown.tone === "past"
              ? `${Math.abs(countdown.days)}d ago`
              : `${countdown.days}d left`
          }
          tone={countdown.tone}
        />
      )}
    </div>
  );
}

function CountdownChip({
  label,
  tone,
}: {
  label: string;
  tone: "ok" | "warn" | "rejected" | "past";
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold whitespace-nowrap",
        tone === "ok" && "bg-status-approved-bg text-status-approved-fg",
        tone === "warn" && "bg-status-review-bg text-status-review-fg",
        tone === "rejected" && "bg-status-rejected-bg text-status-rejected-fg",
        tone === "past" && "bg-sand-100 text-ink-600",
      )}
    >
      <Clock size={11} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Compliance pre-flight (Elm) — ranked next-actions for the owner.
//
// Reads /api/projects/:id/preflight, which returns Claude's coaching
// list (blocker → warning → nice_to_have) for the project draft.
// Renders nothing when the API returns null (no key / call failed)
// so the existing Requirements pills remain the source of truth.
// ---------------------------------------------------------------------------
export function PreflightPanel({ projectId }: { projectId: string }) {
  const [data, setData] = useState<PreflightOutput | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);

  const reload = async () => {
    setLoading(true);
    setStale(false);
    try {
      const r = await api.get<{ data: PreflightOutput | null }>(
        `/api/projects/${projectId}/preflight`,
      );
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  // Mark cached coaching as stale on tab refocus so an owner who
  // signed the indemnity in another tab can come back and re-pull.
  useEffect(() => {
    const onFocus = () => setStale(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  if (!loading && !data) return null;

  const SEVERITY_TONE: Record<PreflightStep["severity"], string> = {
    blocker: "bg-status-rejected-bg text-status-rejected-fg",
    warning: "bg-status-review-bg text-status-review-fg",
    nice_to_have: "bg-sand-100 text-ink-600",
  };
  const SEVERITY_ICON: Record<PreflightStep["severity"], React.ReactNode> = {
    blocker: <AlertTriangle size={11} />,
    warning: <Clock size={11} />,
    nice_to_have: <Lightbulb size={11} />,
  };
  const SEVERITY_LABEL: Record<PreflightStep["severity"], string> = {
    blocker: "Blocker",
    warning: "Recommended",
    nice_to_have: "Polish",
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
          <Sparkles size={16} className="text-brand-700" />
          Elm's pre-flight
        </h2>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className={cx(
            "text-[12px] px-2.5 py-1 rounded-md transition-colors",
            stale && !loading
              ? "bg-brand-50 text-brand-700 hover:bg-brand-100"
              : "text-ink-500 hover:text-ink-700 hover:bg-sand-50",
          )}
        >
          {loading ? "Refreshing…" : stale ? "Re-check" : "Refresh"}
        </button>
      </div>
      {loading && !data ? (
        <p className="text-[13px] text-ink-500">Elm is reviewing your draft…</p>
      ) : data ? (
        <>
          <p className="text-[13px] text-ink-700 mb-3">{data.summary}</p>
          <ol className="flex flex-col gap-2">
            {data.steps.map((step, i) => (
              <li
                key={i}
                className="flex items-start gap-3 px-3.5 py-2.5 bg-sand-50 border border-sand-100 rounded-md"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white border border-sand-200 text-[11px] font-semibold text-ink-700 flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[13px] font-semibold text-ink-800">
                      {step.title}
                    </span>
                    <span
                      className={cx(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                        SEVERITY_TONE[step.severity],
                      )}
                    >
                      {SEVERITY_ICON[step.severity]}
                      {SEVERITY_LABEL[step.severity]}
                    </span>
                  </div>
                  <div className="text-[12.5px] text-ink-700 leading-snug">
                    {step.action}
                  </div>
                  <div className="text-[11.5px] text-ink-500 leading-snug mt-0.5">
                    {step.why}
                  </div>
                  {step.link && (
                    <a
                      href={step.link}
                      target={
                        step.link.startsWith("http") ||
                        step.link.startsWith("/")
                          ? "_blank"
                          : undefined
                      }
                      rel={
                        step.link.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className="inline-flex items-center text-[12px] text-brand-700 hover:text-brand-800 mt-1"
                    >
                      Open →
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <p className="text-[10.5px] text-ink-400 mt-3 leading-snug">
            Elm's pre-flight is advisory — the Requirements card below stays
            the canonical readiness check. Re-check after you make changes.
          </p>
        </>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Construction Rules acknowledgement (owner click-wrap)
// ---------------------------------------------------------------------------
export function RulesAcknowledgementPanel({
  projectId,
  isOwner,
  onChange,
}: {
  projectId: string;
  isOwner: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [state, setState] = useState<RulesAckState | null>(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<RulesAckState>(
        `/api/projects/${projectId}/rules-acknowledgement`,
      );
      setState(r);
    } catch {
      setState(null);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/rules-acknowledgement`, {
        acceptText: "I agree",
      });
      setAgree(false);
      await reload();
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const signed = state?.data?.ownerSignedAt;

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <BookOpenText size={18} /> Construction Rules Acknowledgement
      </h2>

      {state?.rulesUrl && (
        <a
          href={state.rulesUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[13px] text-brand-700 hover:text-brand-800 mb-3"
        >
          <ExternalLink size={14} />
          Read the Construction Rules and Regulations
        </a>
      )}

      {signed ? (
        <div className="alert-success">
          Acknowledged on {new Date(signed).toLocaleString()}.
        </div>
      ) : (
        <>
          <details className="mb-3">
            <summary className="cursor-pointer text-sm text-ink-600 hover:text-ink-800">
              See what you're agreeing to
            </summary>
            <pre className="whitespace-pre-wrap text-sm text-ink-700 bg-ink-50 rounded-lg p-3 mt-2 border border-ink-200">
              {state?.text ?? ""}
            </pre>
          </details>
          {isOwner ? (
            <form onSubmit={submit}>
              <label className="flex items-start gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 rounded border-ink-300 text-brand-700 focus:ring-brand-500"
                />
                <span>
                  I have read the Construction Rules and Regulations and agree
                  to abide by them, and I will ensure my contractors do the
                  same.
                </span>
              </label>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                loading={busy}
                disabled={!agree}
              >
                Acknowledge rules
              </Button>
            </form>
          ) : (
            <p className="text-[13px] text-ink-500">
              The unit owner needs to acknowledge the rules before this
              project can be submitted.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Procedures for Unit Construction Inspection (owner click-wrap)
// ---------------------------------------------------------------------------
type ProceduresAckState = {
  data: { id: string; ownerSignedAt: string | null } | null;
  text: string;
  textHash: string;
};

export function ProceduresAcknowledgementPanel({
  projectId,
  isOwner,
  onChange,
}: {
  projectId: string;
  isOwner: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [state, setState] = useState<ProceduresAckState | null>(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<ProceduresAckState>(
        `/api/projects/${projectId}/procedures-acknowledgement`,
      );
      setState(r);
    } catch {
      setState(null);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/procedures-acknowledgement`, {
        acceptText: "I agree",
      });
      setAgree(false);
      await reload();
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const signed = state?.data?.ownerSignedAt;

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <ClipboardList size={18} /> Procedures for Unit Construction Inspection
      </h2>
      <p className="text-[13px] text-ink-600 mb-3">
        Confirms the three-phase inspection regime: the Chief Engineer signs
        off on each phase before the next begins. Skipping a phase can
        forfeit the security deposit.
      </p>

      {signed ? (
        <div className="alert-success">
          Acknowledged on {new Date(signed).toLocaleString()}.
        </div>
      ) : (
        <>
          <details className="mb-3">
            <summary className="cursor-pointer text-sm text-ink-600 hover:text-ink-800">
              See what you're agreeing to
            </summary>
            <pre className="whitespace-pre-wrap text-sm text-ink-700 bg-ink-50 rounded-lg p-3 mt-2 border border-ink-200">
              {state?.text ?? ""}
            </pre>
          </details>
          {isOwner ? (
            <form onSubmit={submit}>
              <label className="flex items-start gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 rounded border-ink-300 text-brand-700 focus:ring-brand-500"
                />
                <span>
                  I have read the Procedures for Unit Construction Inspection
                  and agree to schedule each phase before advancing the work.
                </span>
              </label>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                loading={busy}
                disabled={!agree}
              >
                Acknowledge procedures
              </Button>
            </form>
          ) : (
            <p className="text-[13px] text-ink-500">
              The unit owner needs to acknowledge the procedures before this
              project can be submitted.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Indemnity panel
// ---------------------------------------------------------------------------
export function IndemnityPanel({
  projectId,
  isOwner,
  onChange,
}: {
  projectId: string;
  isOwner: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [state, setState] = useState<IndemnityState | null>(null);
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<IndemnityState>(
        `/api/projects/${projectId}/indemnity`,
      );
      setState(r);
    } catch {
      setState(null);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/indemnity`, {
        signerFullName: name,
        acceptText: "I agree",
      });
      await reload();
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <FileSignature size={18} /> Indemnity Agreement
      </h2>
      {state?.data ? (
        <div className="alert-success">
          Signed {new Date(state.data.signedAt).toLocaleString()} by{" "}
          <strong>{state.data.signerFullName}</strong>.
        </div>
      ) : (
        <>
          <details className="mb-3">
            <summary className="cursor-pointer text-sm text-ink-600 hover:text-ink-800">
              Read agreement
            </summary>
            <pre className="whitespace-pre-wrap text-sm text-ink-700 bg-ink-50 rounded-lg p-3 mt-2 border border-ink-200">
              {state?.text ?? ""}
            </pre>
          </details>
          {isOwner && (
            <form onSubmit={submit}>
              <Field
                label="Type your full legal name to sign"
                hint="By signing you agree to the indemnity terms above; the agreement is binding on successors in interest to the unit."
              >
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                />
              </Field>
              <label className="flex items-center gap-2 text-sm text-ink-700 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agree}
                  onChange={(e) => setAgree(e.target.checked)}
                  className="rounded border-ink-300 text-brand-700 focus:ring-brand-500"
                />
                I agree to the indemnity terms
              </label>
              {error && <div className="alert-error">{error}</div>}
              <Button
                variant="primary"
                type="submit"
                loading={busy}
                disabled={!agree || name.length < 2}
              >
                Sign indemnity
              </Button>
            </form>
          )}
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Fees / deposit panel
// ---------------------------------------------------------------------------
export function FeesPanel({
  projectId,
  isStaff,
  onChange,
}: {
  projectId: string;
  isStaff: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [items, setItems] = useState<FeeRow[]>([]);
  const [summary, setSummary] = useState<FeeSummary | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<{ data: FeeRow[]; summary: FeeSummary }>(
        `/api/projects/${projectId}/fees`,
      );
      setItems(r.data);
      setSummary(r.summary);
    } catch {
      setItems([]);
      setSummary(null);
    }
  };

  useEffect(() => {
    void reload();
  }, [projectId]);

  const markReceived = async (feeId: string) => {
    await api.post(`/api/fees/${feeId}/mark-received`);
    await reload();
    await onChange();
  };

  if (items.length === 0 && summary === null) return null;

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <Receipt size={18} /> Fees &amp; deposit
      </h2>
      {summary && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Stat label="Outstanding" value={`$${(summary.dueCents / 100).toFixed(2)}`} />
          <Stat label="Received" value={`$${(summary.receivedCents / 100).toFixed(2)}`} />
        </div>
      )}
      <ul className="divide-y divide-ink-100">
        {items.map((f) => (
          <li
            key={f.id}
            className="py-2 flex items-center gap-3 flex-wrap"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium text-ink-800 truncate">
                {f.description}
              </div>
              <div className="text-xs text-ink-500">
                {f.kind} · ${(f.amountCents / 100).toFixed(2)} · due{" "}
                {new Date(f.dueAt).toLocaleDateString()}
              </div>
            </div>
            <span
              className={cx(
                "badge",
                f.status === "received" && "bg-status-approved-bg text-status-approved-fg",
                f.status === "due" && "bg-status-submitted-bg text-status-submitted-fg",
                f.status === "refunded" && "bg-ink-100 text-ink-600",
                f.status === "withheld" && "bg-status-rejected-bg text-status-rejected-fg",
                f.status === "waived" && "bg-ink-100 text-ink-600",
              )}
            >
              {f.status}
            </span>
            {isStaff && f.status === "due" && (
              <Button size="sm" onClick={() => void markReceived(f.id)}>
                Mark received
              </Button>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-ink-400 mt-3">
        Make checks payable to <em>The Elm at Clark Condominium Association</em>.
        Digital payments are not accepted in v1.
      </p>
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-ink-50 border border-ink-200 px-3 py-2">
      <div className="text-xs text-ink-500 uppercase tracking-wider">
        {label}
      </div>
      <div className="text-lg font-semibold text-ink-800">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contractor compliance panel
//
// One row per invited contractor with the magic-link funnel + upload
// count: invited → opened the link → uploaded N docs. Lets owners and
// staff see at a glance which contractors have actually responded
// instead of having to chase them by email.
// ---------------------------------------------------------------------------
export function ContractorsPanel({
  projectId,
}: {
  projectId: string;
}) {
  const [rows, setRows] = useState<ContractorStatus[] | null>(null);

  const reload = () =>
    api
      .get<{ data: ContractorStatus[] }>(
        `/api/projects/${projectId}/contractors-status`,
      )
      .then((r) => setRows(r.data))
      .catch(() => setRows([]));

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const STATUS_COPY: Record<
    ContractorStatus["status"],
    { label: string; tone: string; icon: React.ReactNode }
  > = {
    no_invite: {
      label: "Not invited yet",
      tone: "bg-sand-100 text-ink-600",
      icon: <Clock size={12} />,
    },
    invited: {
      label: "Invite sent",
      tone: "bg-status-submitted-bg text-status-submitted-fg",
      icon: <MailCheck size={12} />,
    },
    viewed: {
      label: "Opened, no uploads yet",
      tone: "bg-status-review-bg text-status-review-fg",
      icon: <MailOpen size={12} />,
    },
    uploading: {
      label: "Uploading documents",
      tone: "bg-status-approved-bg text-status-approved-fg",
      icon: <UploadIcon size={12} />,
    },
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-3">
        <Hammer size={18} /> Contractor team
      </h2>
      {rows === null ? (
        <p className="text-ink-500 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-ink-500 text-sm">
          No contractors invited yet. Add one from the new-project wizard or
          re-open the draft to add them.
        </p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {rows.map((c) => {
            const copy = STATUS_COPY[c.status];
            const linkExpired =
              c.link &&
              !c.link.exchangedAt &&
              new Date(c.link.expiresAt).getTime() < Date.now();
            return (
              <li key={c.projectContractorId} className="py-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-ink-800 truncate">
                      {c.companyName}
                      <span className="ml-2 text-[11px] font-normal uppercase tracking-wider text-ink-400">
                        {c.role}
                      </span>
                    </div>
                    <div className="text-xs text-ink-500 mt-0.5">
                      {c.companyEmail ?? c.link?.invitedEmail ?? "—"}
                    </div>
                  </div>
                  <span
                    className={cx(
                      "inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium",
                      copy.tone,
                    )}
                  >
                    {copy.icon}
                    {copy.label}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2.5 text-[11.5px]">
                  <ContractorStat
                    label="Added"
                    value={formatRelative(c.addedAt)}
                    title={new Date(c.addedAt).toLocaleString()}
                  />
                  <ContractorStat
                    label="Invite sent"
                    value={
                      c.link ? formatRelative(c.link.issuedAt) : "Not sent"
                    }
                    title={
                      c.link
                        ? new Date(c.link.issuedAt).toLocaleString()
                        : undefined
                    }
                  />
                  <ContractorStat
                    label="Link opened"
                    value={
                      c.link?.exchangedAt
                        ? formatRelative(c.link.exchangedAt)
                        : linkExpired
                          ? "Expired"
                          : "Not yet"
                    }
                    title={
                      c.link?.exchangedAt
                        ? new Date(c.link.exchangedAt).toLocaleString()
                        : undefined
                    }
                  />
                  <ContractorStat
                    label="Documents"
                    value={
                      c.documentCount === 0
                        ? "0 uploaded"
                        : `${c.documentCount} · last ${formatRelative(c.latestUploadAt)}`
                    }
                    title={
                      c.latestUploadAt
                        ? new Date(c.latestUploadAt).toLocaleString()
                        : undefined
                    }
                  />
                </div>
                {linkExpired && (
                  <p className="text-[11.5px] text-status-rejected-fg mt-2">
                    The single-use link expired before the contractor opened
                    it. Resend below to issue a fresh one.
                  </p>
                )}
                {c.status !== "uploading" && (
                  <ContractorResendButton
                    projectId={projectId}
                    projectContractorId={c.projectContractorId}
                    onChange={reload}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Per-contractor Resend action.
//
// POSTs to the resend-invite route and waits for confirmation. Tokens
// are stored as SHA-256 of the plaintext, so resend always rotates:
// the prior open link is revoked, a fresh link is issued, and the
// new URL goes out by email. Cooldown is 5 minutes per project +
// company; the server returns 429 with retryAfterSeconds when a
// resend is too soon.
// ---------------------------------------------------------------------------
function ContractorResendButton({
  projectId,
  projectContractorId,
  onChange,
}: {
  projectId: string;
  projectContractorId: string;
  onChange: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.post(
        `/api/projects/${projectId}/contractors/${projectContractorId}/resend-invite`,
      );
      setDone(true);
      await onChange();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        const body = err.body as { retryAfterSeconds?: number } | null;
        const seconds = body?.retryAfterSeconds ?? 300;
        setError(
          `Cooldown active. Try again in about ${Math.ceil(seconds / 60)} minute(s).`,
        );
      } else if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setError(
          body?.error === "already_exchanged"
            ? "This contractor already opened the original link."
            : "Cannot resend right now.",
        );
      } else {
        setError(
          err instanceof ApiError ? JSON.stringify(err.body) : String(err),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2.5 flex items-center gap-3 flex-wrap">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => void resend()}
        loading={busy}
        disabled={busy}
      >
        <RefreshCcw size={13} />
        Resend invite
      </Button>
      {done && !error && (
        <span className="text-[11.5px] text-status-approved-fg">
          Fresh invite emailed.
        </span>
      )}
      {error && (
        <span className="text-[11.5px] text-status-rejected-fg">{error}</span>
      )}
    </div>
  );
}

function ContractorStat({
  label,
  value,
  title,
}: {
  label: string;
  value: string;
  title?: string;
}) {
  return (
    <div title={title} className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-ink-400">
        {label}
      </div>
      <div className="text-ink-700 truncate">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Riser shutdown impact list — shows the units sharing the selected
// riser stack so the owner sees pre-submit which neighbors will be
// notified when the shutdown is approved. Includes a one-line
// synthesis: \"Affects N other units on stack <code> · earliest
// available <date> · $150 fee\" so owners answer the \"wait, who
// does this affect and when?\" question without scrolling.
// ---------------------------------------------------------------------------
type RiserPreview = {
  riser: { id: string; code: string; name: string };
  affectedUnits: { id: string; unitNumber: string; floor: number | null }[];
  earliestAvailableDate: string;
  conflicts: string[];
  feeCents: number;
  rescheduleFeeCents: number;
  advanceNoticeHours: number;
};

export function RiserImpactList({
  buildingId,
  riserId,
  currentUnitId,
}: {
  buildingId: string;
  riserId: string;
  currentUnitId?: string;
}) {
  const [preview, setPreview] = useState<RiserPreview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setPreview(null);
    setError(false);
    if (!riserId) return;
    void api
      .get<RiserPreview>(
        `/api/buildings/${buildingId}/risers/${riserId}/preview`,
      )
      .then(setPreview)
      .catch(() => setError(true));
  }, [buildingId, riserId]);

  if (error) {
    return (
      <p className="text-[12px] text-ink-500 -mt-1 mb-3">
        Couldn't load the riser impact preview.
      </p>
    );
  }
  if (!preview) {
    return (
      <p className="text-[12px] text-ink-500 -mt-1 mb-3">
        Loading affected units…
      </p>
    );
  }
  const rows = preview.affectedUnits;
  if (rows.length === 0) {
    return (
      <p className="text-[12px] text-ink-500 -mt-1 mb-3">
        No units found on this riser stack.
      </p>
    );
  }
  const otherCount = currentUnitId
    ? rows.filter((u) => u.id !== currentUnitId).length
    : rows.length;

  return (
    <div className="bg-status-review-bg/50 border border-status-review-fg/20 rounded-md px-3 py-2.5 -mt-1 mb-3">
      <div className="text-[11px] font-semibold tracking-wider uppercase text-ink-500 mb-1.5">
        Notification impact
      </div>
      <p className="text-[12.5px] text-ink-800 font-medium leading-snug mb-1">
        Affects {otherCount} other unit{otherCount === 1 ? "" : "s"} on
        stack {preview.riser.code}
        {" · "}earliest available{" "}
        {new Date(preview.earliestAvailableDate).toLocaleDateString()}
        {" · "}${(preview.feeCents / 100).toFixed(0)} fee
      </p>
      <p className="text-[11.5px] text-ink-500 leading-snug mb-2">
        72-hour minimum advance notice; ${preview.rescheduleFeeCents / 100}{" "}
        reschedule fee. On approval every owner in this stack receives an
        email with the date and reason — neighbors don't love
        water-shut-off surprises.
        {preview.conflicts.length > 0 && (
          <>
            {" "}
            Already approved on this riser:{" "}
            {preview.conflicts
              .map((d) => new Date(d).toLocaleDateString())
              .join(", ")}
            .
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((u) => (
          <span
            key={u.id}
            className={cx(
              "inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium",
              u.id === currentUnitId
                ? "bg-brand-100 text-brand-800 ring-1 ring-brand-300"
                : "bg-white border border-sand-200 text-ink-700",
            )}
            title={u.id === currentUnitId ? "Your unit" : undefined}
          >
            {u.unitNumber}
            {u.id === currentUnitId && " · you"}
          </span>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Telecom shutdown impact hint — quick rule recap shown next to the
// telecom checkbox in the wizard. The actual schedule + system pick
// happens on the project page after save.
// ---------------------------------------------------------------------------
export function TelecomImpactHint() {
  return (
    <div className="bg-status-review-bg/50 border border-status-review-fg/20 rounded-md px-3 py-2.5 -mt-1 mb-3 text-[12px] text-ink-700 leading-snug">
      <span className="font-medium text-ink-800">Telecom shutdowns:</span>{" "}
      first per system (telephone, cable TV, master antenna) is free; each
      subsequent is $100. Available 10 AM – 3 PM Mon–Fri only with 3 working
      days advance notice. Schedule the request from the project page after
      you save the draft.
    </div>
  );
}

// ---------------------------------------------------------------------------
// Riser shutdown panel
// ---------------------------------------------------------------------------
export function RiserShutdownPanel({
  projectId,
  buildingId,
  unitId,
  isOwner,
  isStaff,
  onChange,
}: {
  projectId: string;
  buildingId: string;
  /** When provided, the riser dropdown defaults to this unit's riser. */
  unitId?: string;
  isOwner: boolean;
  isStaff: boolean;
  onChange: () => void | Promise<void>;
}) {
  const [items, setItems] = useState<RiserShutdownRow[]>([]);
  const [risers, setRisers] = useState<
    { id: string; code: string; name: string }[]
  >([]);
  const [form, setForm] = useState({
    riserId: "",
    requestedDate: "",
    area: "",
    purpose: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await api.get<{ data: RiserShutdownRow[] }>(
        `/api/projects/${projectId}/riser-shutdowns`,
      );
      setItems(r.data);
    } catch {
      setItems([]);
    }
  };

  useEffect(() => {
    void reload();
    void api
      .get<{ data: { id: string; code: string; name: string }[] }>(
        `/api/buildings/${buildingId}/risers`,
      )
      .then((r) => setRisers(r.data))
      .catch(() => setRisers([]));
    // Default the riser dropdown to whichever riser this unit lives
    // on. Owners almost always shut down their own unit's water riser
    // — pre-selecting it removes a needless click and a class of
    // wrong-riser submissions.
    if (unitId) {
      void api
        .get<{ data: { id: string; riserId: string }[] }>(
          `/api/buildings/${buildingId}/units`,
        )
        .then((r) => {
          const u = r.data.find((row) => row.id === unitId);
          if (u?.riserId) {
            setForm((prev) =>
              prev.riserId ? prev : { ...prev, riserId: u.riserId },
            );
          }
        })
        .catch(() => {});
    }
  }, [projectId, buildingId, unitId]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/projects/${projectId}/riser-shutdowns`, form);
      // Keep the unit's riser pre-selected after submit; owners
      // submitting a second shutdown for the same unit don't have to
      // re-pick it.
      setForm((prev) => ({
        riserId: prev.riserId,
        requestedDate: "",
        area: "",
        purpose: "",
      }));
      await reload();
      await onChange();
    } catch (err) {
      setError(
        err instanceof ApiError ? JSON.stringify(err.body) : String(err),
      );
    } finally {
      setBusy(false);
    }
  };

  const dispose = async (id: string, approve: boolean) => {
    await api.post(`/api/riser-shutdowns/${id}/dispose`, {
      approve,
      scheduledDate: approve
        ? new Date().toISOString().slice(0, 10)
        : undefined,
    });
    await reload();
    await onChange();
  };

  return (
    <Card>
      <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
        <Droplet size={18} /> Water riser shutdowns
      </h2>
      <p className="text-xs text-ink-500 mb-3">
        72 hours advance notice required. $150 per shutdown · $20 per
        reschedule. Affected units in the riser stack are notified on
        approval.
      </p>
      {items.length === 0 ? (
        <p className="text-ink-500 text-sm">None requested.</p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {items.map((r) => (
            <li key={r.id} className="py-2 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-ink-800">
                  {r.area} · {r.requestedDate}
                </div>
                <div className="text-xs text-ink-500 truncate">{r.purpose}</div>
              </div>
              <span
                className={cx(
                  "badge",
                  r.disposition === "approved" &&
                    "bg-status-approved-bg text-status-approved-fg",
                  r.disposition === "rejected" &&
                    "bg-status-rejected-bg text-status-rejected-fg",
                  r.disposition === "pending" &&
                    "bg-status-submitted-bg text-status-submitted-fg",
                )}
              >
                {r.disposition}
              </span>
              {isStaff && r.disposition === "pending" && (
                <>
                  <Button size="sm" onClick={() => void dispose(r.id, true)}>
                    <Check size={14} /> Approve
                  </Button>
                  <Button size="sm" onClick={() => void dispose(r.id, false)}>
                    <X size={14} /> Reject
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOwner && (
        <form onSubmit={create} className="mt-4">
          <h3 className="text-ink-800 mb-2 inline-flex items-center gap-2">
            <ShieldCheck size={16} />
            Request a shutdown
          </h3>
          <Field label="Riser stack">
            <select
              className="select"
              value={form.riserId}
              onChange={(e) => setForm({ ...form, riserId: e.target.value })}
              required
            >
              <option value="">Choose…</option>
              {risers.map((r) => (
                <option key={r.id} value={r.id}>
                  Riser {r.code} — {r.name}
                </option>
              ))}
            </select>
          </Field>
          {form.riserId && (
            <RiserImpactList
              buildingId={buildingId}
              riserId={form.riserId}
              currentUnitId={unitId}
            />
          )}
          <div className="field-row">
            <Field label="Requested date">
              <input
                className="input"
                type="date"
                value={form.requestedDate}
                onChange={(e) =>
                  setForm({ ...form, requestedDate: e.target.value })
                }
                required
              />
            </Field>
            <Field label="Area">
              <input
                className="input"
                value={form.area}
                onChange={(e) => setForm({ ...form, area: e.target.value })}
                placeholder="e.g. master bath"
                required
              />
            </Field>
          </div>
          <Field label="Purpose">
            <textarea
              className="textarea"
              value={form.purpose}
              onChange={(e) => setForm({ ...form, purpose: e.target.value })}
              rows={2}
              required
            />
          </Field>
          {error && <div className="alert-error">{error}</div>}
          <Button variant="primary" type="submit" loading={busy}>
            Request shutdown
          </Button>
        </form>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Staff anomaly panel — Elm second-pair-of-eyes flagging for PMs
// reviewing a project. Renders a short ranked list of "things worth
// a look" (missing plumber on a kitchen, scope/type drift, Cat C
// missing License Agreement, etc.). Read-only, advisory. Quietly
// hides when the LLM isn't available so PMs without a key see the
// existing review UI unchanged.
// ---------------------------------------------------------------------------
export function AnomalyPanel({ projectId }: { projectId: string }) {
  const [data, setData] = useState<AnomalyOutput | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get<{ data: AnomalyOutput | null }>(
        `/api/management/projects/${projectId}/anomalies`,
      );
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (!loading && !data) return null;
  // No anomalies + non-empty summary still renders so PMs see
  // "Looks clean." reassurance — but only when the model returned
  // something. Hide entirely on null (no key / failure).
  if (data && data.anomalies.length === 0) {
    return (
      <Card>
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-brand-700" />
          Elm anomaly check
        </h2>
        <p className="text-[12.5px] text-ink-700 mb-0 inline-flex items-center gap-2">
          <Check size={13} className="text-status-approved-fg" />
          {data.summary || "Looks clean."}
        </p>
      </Card>
    );
  }

  const SEVERITY_TONE: Record<Anomaly["severity"], string> = {
    high: "bg-status-rejected-bg text-status-rejected-fg",
    medium: "bg-status-review-bg text-status-review-fg",
    low: "bg-sand-100 text-ink-600",
  };
  const SEVERITY_LABEL: Record<Anomaly["severity"], string> = {
    high: "High",
    medium: "Medium",
    low: "Low",
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
          <Sparkles size={16} className="text-brand-700" />
          Elm anomaly check
          <span className="badge bg-ink-100 text-ink-600 text-[10px]">
            staff only
          </span>
        </h2>
        <button
          type="button"
          onClick={() => void reload()}
          disabled={loading}
          className="text-[12px] text-ink-500 hover:text-ink-700 hover:bg-sand-50 px-2.5 py-1 rounded-md"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {loading && !data ? (
        <p className="text-[13px] text-ink-500">Elm is reviewing the project…</p>
      ) : data ? (
        <>
          <p className="text-[13px] text-ink-700 mb-3">{data.summary}</p>
          <ul className="flex flex-col gap-2">
            {data.anomalies.map((a, i) => (
              <li
                key={i}
                className="flex items-start gap-3 px-3.5 py-2.5 bg-sand-50 border border-sand-100 rounded-md"
              >
                <AlertTriangle
                  size={14}
                  className={cx(
                    "mt-0.5 flex-shrink-0",
                    a.severity === "high"
                      ? "text-status-rejected-fg"
                      : a.severity === "medium"
                        ? "text-status-review-fg"
                        : "text-ink-400",
                  )}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-[13px] font-semibold text-ink-800">
                      {a.title}
                    </span>
                    <span
                      className={cx(
                        "inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                        SEVERITY_TONE[a.severity],
                      )}
                    >
                      {SEVERITY_LABEL[a.severity]}
                    </span>
                  </div>
                  <div className="text-[12.5px] text-ink-700 leading-snug">
                    {a.detail}
                  </div>
                  <div className="text-[11.5px] text-ink-500 leading-snug mt-1 inline-flex items-start gap-1">
                    <Lightbulb size={11} className="mt-0.5 flex-shrink-0" />
                    <span>{a.suggestedAction}</span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="text-[10.5px] text-ink-400 mt-3 leading-snug">
            Advisory only — Elm doesn't gate approval. Use your judgement.
          </p>
        </>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Conditional-approval explanation panel.
//
// Visible to owner + staff on a project that's been
// conditionally_approved. Reads the Board's free-form conditions
// text, asks Elm to render it as actionable steps with severity +
// where-to-go links, and falls back to the raw text when the LLM
// isn't available. The raw text always remains visible in a
// collapsed disclosure so nothing the Board wrote is hidden.
// ---------------------------------------------------------------------------
export function ConditionsExplanationPanel({
  projectId,
}: {
  projectId: string;
}) {
  const [env, setEnv] = useState<ConditionsExplanationEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get<ConditionsExplanationEnvelope>(
        `/api/projects/${projectId}/conditions/explanation`,
      );
      setEnv(r);
    } catch {
      setEnv(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading || !env) return null;
  // No conditional approval on this project — render nothing.
  if (env.reason === "not_conditionally_approved") return null;
  // Approval exists but neither the rendered explanation nor any raw
  // text — nothing useful to show.
  if (!env.data && !env.rawConditions) return null;

  const SEVERITY_TONE: Record<ExplainedCondition["severity"], string> = {
    blocker: "bg-status-rejected-bg text-status-rejected-fg",
    warning: "bg-status-review-bg text-status-review-fg",
    info: "bg-sand-100 text-ink-600",
  };
  const SEVERITY_ICON: Record<
    ExplainedCondition["severity"],
    React.ReactNode
  > = {
    blocker: <AlertTriangle size={11} />,
    warning: <Clock size={11} />,
    info: <Lightbulb size={11} />,
  };
  const SEVERITY_LABEL: Record<ExplainedCondition["severity"], string> = {
    blocker: "Must address",
    warning: "Address before scheduling",
    info: "Heads-up",
  };

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <h2 className="text-ink-800 m-0 inline-flex items-center gap-2">
          <ShieldCheck size={16} className="text-status-review-fg" />
          Board conditions
          <span className="badge bg-status-review-bg text-status-review-fg text-[10px]">
            conditionally approved
          </span>
        </h2>
      </div>
      {env.data ? (
        <>
          <p className="text-[13px] text-ink-700 mb-3">{env.data.summary}</p>
          <ol className="flex flex-col gap-2">
            {env.data.conditions.map((cond, i) => (
              <li
                key={i}
                className="flex items-start gap-3 px-3.5 py-2.5 bg-sand-50 border border-sand-100 rounded-md"
              >
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white border border-sand-200 text-[11px] font-semibold text-ink-700 flex-shrink-0">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[13px] font-semibold text-ink-800">
                      {cond.title}
                    </span>
                    <span
                      className={cx(
                        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold",
                        SEVERITY_TONE[cond.severity],
                      )}
                    >
                      {SEVERITY_ICON[cond.severity]}
                      {SEVERITY_LABEL[cond.severity]}
                    </span>
                  </div>
                  <div className="text-[12.5px] text-ink-700 leading-snug">
                    {cond.meaning}
                  </div>
                  <div className="text-[11.5px] text-ink-500 leading-snug mt-1 inline-flex items-start gap-1">
                    <Lightbulb size={11} className="mt-0.5 flex-shrink-0" />
                    <span>{cond.howToAddress}</span>
                  </div>
                  {cond.link && (
                    <a
                      href={cond.link}
                      target={
                        cond.link.startsWith("http") ||
                        cond.link.startsWith("/")
                          ? "_blank"
                          : undefined
                      }
                      rel={
                        cond.link.startsWith("http")
                          ? "noopener noreferrer"
                          : undefined
                      }
                      className="inline-flex items-center text-[12px] text-brand-700 hover:text-brand-800 mt-1"
                    >
                      Open →
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ol>
          {env.data.whoToContact && (
            <p className="text-[12px] text-ink-600 mt-3 mb-0 leading-snug inline-flex items-start gap-1.5">
              <BookOpenText size={12} className="mt-0.5 flex-shrink-0" />
              <span>{env.data.whoToContact}</span>
            </p>
          )}
        </>
      ) : (
        <p className="text-[12.5px] text-ink-600 mb-2">
          The Board recorded conditions on this approval. Review the raw text
          below.
        </p>
      )}
      {env.rawConditions ? (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowRaw((v) => !v)}
            className="text-[11.5px] text-ink-500 hover:text-ink-700 inline-flex items-center gap-1"
          >
            {showRaw ? "Hide" : "Show"} raw conditions text
          </button>
          {showRaw && (
            <pre className="text-[12px] text-ink-700 bg-white border border-sand-200 rounded-md p-2.5 mt-1.5 whitespace-pre-wrap font-sans m-0">
              {env.rawConditions}
            </pre>
          )}
        </div>
      ) : null}
    </Card>
  );
}
